import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  BookOpen, Heart, Sparkles, HandHeart, ChevronRight, 
  ChevronLeft, Check, X
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = [
  { id: 'verse', label: 'Verse', icon: BookOpen, color: 'bg-blue-100 text-blue-500' },
  { id: 'devotional', label: 'Devotional', icon: Heart, color: 'bg-pink-100 text-pink-500' },
  { id: 'prayer', label: 'Prayer', icon: Sparkles, color: 'bg-amber-100 text-amber-500' },
  { id: 'challenge', label: 'Kindness', icon: HandHeart, color: 'bg-green-100 text-green-500' },
];

export default function Session() {
  const navigate = useNavigate();
  const [dailyPlan, setDailyPlan] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(false);

  useEffect(() => {
    fetchPlan();
  }, []);

  const fetchPlan = async () => {
    try {
      const response = await axios.get(`${API}/daily/plan`);
      setDailyPlan(response.data);
      
      const completedSteps = response.data.completed_steps || [];
      const firstIncomplete = STEPS.findIndex(s => !completedSteps.includes(s.id));
      if (firstIncomplete !== -1) {
        setCurrentStep(firstIncomplete);
      }
    } catch (error) {
      toast.error('Could not load session');
      navigate('/home');
    } finally {
      setLoading(false);
    }
  };

  const completeStep = async () => {
    if (!dailyPlan) return;
    
    setCompleting(true);
    const stepId = STEPS[currentStep].id;
    
    try {
      await axios.post(`${API}/daily/complete-step?step=${stepId}`);
      
      if (currentStep < STEPS.length - 1) {
        setCurrentStep(currentStep + 1);
        toast.success(`${STEPS[currentStep].label} complete!`);
      } else {
        toast.success('Session complete! Amazing job! 🎉💙');
        navigate('/home');
      }
    } catch (error) {
      toast.error('Could not save progress');
    } finally {
      setCompleting(false);
    }
  };

  const goBack = () => {
    if (currentStep > 0) setCurrentStep(currentStep - 1);
  };

  const progressPercent = ((currentStep + 1) / STEPS.length) * 100;

  const renderStepContent = () => {
    if (!dailyPlan) return null;

    const step = STEPS[currentStep];

    switch (step.id) {
      case 'verse':
        return (
          <div className="space-y-6 animate-fade-in" data-testid="session-verse">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-4">
                <BookOpen className="w-8 h-8 text-blue-500" />
              </div>
              <h2 className="text-xl font-bold text-gray-800 mb-1">Today&apos;s Verse</h2>
              <p className="text-gray-500 text-sm">Let God&apos;s word speak to you</p>
            </div>
            <Card className="rounded-3xl bg-gradient-to-br from-blue-50 to-sky-50 border-0 shadow-sm">
              <CardContent className="p-6">
                <blockquote className="text-xl font-medium text-gray-800 leading-relaxed mb-4">
                  &ldquo;{dailyPlan.verse}&rdquo;
                </blockquote>
                <p className="text-blue-600 font-semibold text-right">— {dailyPlan.verse_reference}</p>
              </CardContent>
            </Card>
            <div className="bg-white rounded-2xl p-4 shadow-sm">
              <p className="text-gray-600 leading-relaxed">
                {dailyPlan.explanation}
              </p>
            </div>
          </div>
        );

      case 'devotional':
        return (
          <div className="space-y-6 animate-fade-in" data-testid="session-devotional">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-pink-100 flex items-center justify-center mx-auto mb-4">
                <Heart className="w-8 h-8 text-pink-500" />
              </div>
              <h2 className="text-xl font-bold text-gray-800 mb-1">Devotional</h2>
              <p className="text-gray-500 text-sm">Take a moment to reflect</p>
            </div>
            <Card className="rounded-3xl border-0 bg-white shadow-sm">
              <CardContent className="p-6">
                <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                  {dailyPlan.devotional_text}
                </p>
              </CardContent>
            </Card>
          </div>
        );

      case 'prayer':
        return (
          <div className="space-y-6 animate-fade-in" data-testid="session-prayer">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
                <Sparkles className="w-8 h-8 text-amber-500" />
              </div>
              <h2 className="text-xl font-bold text-gray-800 mb-1">Guided Prayer</h2>
              <p className="text-gray-500 text-sm">Speak from your heart</p>
            </div>
            <Card className="rounded-3xl bg-gradient-to-br from-amber-50 to-yellow-50 border-0 shadow-sm">
              <CardContent className="p-6">
                <p className="text-lg text-gray-800 leading-relaxed italic">
                  {dailyPlan.prayer_text}
                </p>
              </CardContent>
            </Card>
            <p className="text-center text-gray-500 text-sm">
              🙏 Take a moment to be still and listen...
            </p>
          </div>
        );

      case 'challenge':
        return (
          <div className="space-y-6 animate-fade-in" data-testid="session-challenge">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                <HandHeart className="w-8 h-8 text-green-500" />
              </div>
              <h2 className="text-xl font-bold text-gray-800 mb-1">Kindness Challenge</h2>
              <p className="text-gray-500 text-sm">Spread love today</p>
            </div>
            <Card className="rounded-3xl bg-gradient-to-br from-green-50 to-emerald-50 border-0 shadow-sm">
              <CardContent className="p-6 text-center">
                <p className="text-xl font-medium text-gray-800 leading-relaxed">
                  {dailyPlan.challenge_text}
                </p>
              </CardContent>
            </Card>
            <p className="text-center text-gray-500 text-sm">
              💝 Small acts create big ripples
            </p>
          </div>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen sky-gradient flex items-center justify-center">
        <div className="animate-gentle-pulse">
          <img src="/images/logo.png" alt="BlessBe" className="w-20 h-20 rounded-full" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen sky-gradient-light flex flex-col" data-testid="session-page">
      {/* Header */}
      <header className="px-6 pt-6 pb-4">
        <div className="flex items-center justify-between mb-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/home')}
            className="rounded-full bg-white/80 shadow-sm hover:bg-white"
            data-testid="close-session"
          >
            <X className="w-5 h-5 text-gray-600" />
          </Button>
          <span className="text-sm font-semibold text-gray-600">
            Step {currentStep + 1} of {STEPS.length}
          </span>
          <div className="w-10" />
        </div>
        <Progress value={progressPercent} className="h-2" />
      </header>

      {/* Step Indicators */}
      <div className="px-6 py-3">
        <div className="flex justify-center gap-3">
          {STEPS.map((step, idx) => {
            const Icon = step.icon;
            const isComplete = idx < currentStep;
            const isCurrent = idx === currentStep;
            return (
              <div
                key={step.id}
                className={`w-12 h-12 rounded-full flex items-center justify-center transition-all shadow-sm ${
                  isComplete ? 'bg-blue-500 text-white' :
                  isCurrent ? step.color :
                  'bg-white text-gray-300'
                }`}
              >
                {isComplete ? <Check className="w-6 h-6" /> : <Icon className="w-6 h-6" />}
              </div>
            );
          })}
        </div>
      </div>

      {/* Content */}
      <main className="flex-1 px-6 py-4 overflow-y-auto">
        {renderStepContent()}
      </main>

      {/* Navigation */}
      <footer className="px-6 py-6 bg-white/80 backdrop-blur-lg border-t border-gray-100">
        <div className="flex gap-3">
          {currentStep > 0 && (
            <Button
              variant="ghost"
              onClick={goBack}
              className="rounded-full text-gray-600"
              data-testid="prev-step"
            >
              <ChevronLeft className="w-5 h-5 mr-1" />
              Back
            </Button>
          )}
          <Button
            onClick={completeStep}
            disabled={completing}
            className="flex-1 h-12 rounded-full text-lg font-bold bg-blue-500 hover:bg-blue-600 shadow-lg"
            data-testid="next-step"
          >
            {completing ? (
              'Saving...'
            ) : currentStep === STEPS.length - 1 ? (
              <>
                Complete Session
                <Check className="w-5 h-5 ml-2" />
              </>
            ) : (
              <>
                Continue
                <ChevronRight className="w-5 h-5 ml-1" />
              </>
            )}
          </Button>
        </div>
      </footer>
    </div>
  );
}
